import { z } from 'zod';

/**
 * MATCH (Mastercard terminated-merchant database) inquiry report shapes.
 *
 * Single source of truth shared by:
 *  - backoffice-v2 `match-inquiries/fetchers.ts` — runtime validation of the API,
 *  - `@ballerine/ui` match-inquiry report template — prop types via `import type`.
 *
 * Keeps the UI Zod-free while guaranteeing the two can't drift (BAL-7464).
 * Mirrors how the TXA template infers from a shared schema package.
 */

export const MatchPropertyMatchTypeSchema = z.enum(['exact', 'partial', 'none']);
export type MatchPropertyMatchType = z.infer<typeof MatchPropertyMatchTypeSchema>;

export const MatchPropertyFieldSchema = z.object({
  field: z.string(),
  value: z.string().nullable().optional(),
  matchType: MatchPropertyMatchTypeSchema,
});
export type MatchPropertyField = z.infer<typeof MatchPropertyFieldSchema>;

export const MatchPrincipalSchema = z.object({
  label: z.string(),
  fields: z.array(MatchPropertyFieldSchema),
});
export type MatchPrincipal = z.infer<typeof MatchPrincipalSchema>;

/**
 * The expandable "Matching Properties" block on a matched record — which
 * merchant fields and which principals matched, with exact/partial indicators.
 */
export const MatchingPropertiesSchema = z.object({
  merchantInformation: z.array(MatchPropertyFieldSchema),
  principals: z.array(MatchPrincipalSchema),
});
export type MatchingPropertiesData = z.infer<typeof MatchingPropertiesSchema>;

export const MatchTerminationReasonSchema = z.object({
  code: z.string(),
  label: z.string(),
});
export type MatchTerminationReason = z.infer<typeof MatchTerminationReasonSchema>;

export const MatchTerminatedMatchSchema = z.object({
  name: z.string(),
  exactMatches: z.number(),
  partialMatches: z.number(),
  terminationReason: MatchTerminationReasonSchema,
  dateAdded: z.string(),
  // ICA of the acquirer that reported the termination (Mastercard AddedByAcquirerID).
  reportingIca: z.string().nullable().optional(),
  matchingProperties: MatchingPropertiesSchema.nullable().optional(),
});
export type MatchTerminatedMatch = z.infer<typeof MatchTerminatedMatchSchema>;

export const MatchInquirySummarySchema = z.object({
  terminatedMatchesCount: z.number(),
  checkDate: z.string(),
  // Raw connector response, surfaced behind the "View" link.
  rawJson: z.unknown().nullable().optional(),
});
export type MatchInquirySummary = z.infer<typeof MatchInquirySummarySchema>;

export const MatchProvidedDetailsSchema = z.object({
  merchantName: z.string().nullable().optional(),
  dba: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  mcc: z.string().nullable().optional(),
});
export type MatchProvidedDetails = z.infer<typeof MatchProvidedDetailsSchema>;

/** The renderable MATCH report body (consumed by the @ballerine/ui template). */
export const MatchInquiryReportDataSchema = z.object({
  summary: MatchInquirySummarySchema.nullable().optional(),
  terminatedMatches: z.array(MatchTerminatedMatchSchema).optional(),
  providedDetails: MatchProvidedDetailsSchema.nullable().optional(),
});
export type MatchInquiryReportData = z.infer<typeof MatchInquiryReportDataSchema>;
